import { RefreshCw, CalendarRange, CalendarDays, type LucideIcon } from 'lucide-react';

export interface BankAccountInfo {
  bank_name?: string;
  last4?: string;
}

export function formatPaymentMethod(method: string, bank?: BankAccountInfo): string {
  if (method === 'ach') {
    if (bank?.bank_name || bank?.last4) {
      const name = bank.bank_name ?? 'Bank';
      return bank.last4 ? `${name} ···· ${bank.last4}` : name;
    }
    return 'Bank Transfer';
  }
  if (method === 'bhcs_cash' || method === 'wallet') return 'BHCS Cash';
  if (method === 'card') return 'Card';
  if (method === 'zelle') return 'Zelle';
  return method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Program-type badge classes live with the rest of the program-type metadata in
// types/program.ts; re-exported here so existing callers keep their import path.
export { getProgramTypeBadgeClasses as getProgramTypeColors } from '../types/program';

interface BillingTypeDisplay {
  label: string;
  Icon: LucideIcon;
}

const BILLING_TYPE_MAP: Record<string, BillingTypeDisplay> = {
  recurring_monthly: { label: 'Monthly', Icon: RefreshCw },
  fixed_term:        { label: 'Fixed Term', Icon: CalendarRange },
  per_day:           { label: 'Per Day', Icon: CalendarDays },
  flex_monthly:      { label: 'Flex Monthly', Icon: CalendarDays },
};

export function getBillingTypeDisplay(billingType: string): BillingTypeDisplay {
  return BILLING_TYPE_MAP[billingType] ?? {
    label: billingType.replace(/_/g, ' '),
    Icon: CalendarRange,
  };
}

/**
 * Line-item config subtypes that are always charged in full, even on a prorated
 * invoice. These are added outside the recurring pricing loop (deposit, late fee,
 * processing fee) or explicitly flagged flat (non_prorated, e.g. performance fee),
 * plus coupon discounts which are a flat $ off. Mirrors the proration rules in the
 * `create-recurring-monthly-invoice` edge function (`shouldProrate = subtype !== 'non_prorated'`).
 */
export const NON_PRORATED_SUBTYPES = new Set([
  'non_prorated',
  'Deposit',
  'late_fee',
  'processing',
  'coupon',
]);

/**
 * Whether a single line item should display a "prorated" badge. A line is prorated
 * only when the invoice itself is prorated AND the item is one billing actually
 * prorates: not materials (one-off) and not a flat-by-nature subtype. Note this keys
 * off subtype, not type — most `fee` subtypes (transportation, activity, enrichment…)
 * ARE prorated, only the flat ones above are excluded.
 */
export function isLineItemProrated(
  invoiceIsProrated: boolean,
  config?: { type?: string; subtype?: string | null },
): boolean {
  return invoiceIsProrated
    && config?.type !== 'materials'
    && !NON_PRORATED_SUBTYPES.has(config?.subtype ?? '');
}
